import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';

import {
  NOTIFICATION_TYPES,
  REVALIDATE_MODES,
} from '@savgroup-front-common/constants';
import { buildNotification } from '@savgroup-front-common/core/helpers';
import { isFileType } from '@savgroup-front-common/core/helpers/AdditionalInformations/isFileType';
import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { useRoutedStepsOrchestratorContext } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import { AdditionalInformation, RELATED_TO } from '@/types';

import useGetClaimGroupSummary from '../hooks/useGetClaimGroupSummary';
import { IrshStepValues } from '../IrshPages.types';

import {
  downloadFile,
  uploadAdditionalInformationFilesToClaim,
} from './helpers/document.adapters';
import { reasonAdapter } from './helpers/reason.adapters';
import messages from './messages';
import newClaimGroupDocumentPageRelatedToClaimSchema from './NewClaimGroupDocumentPageRelatedToClaim.schema';
import { ClaimGroupDocumentRelatedToClaimValues } from './NewClaimGroupDocumentPageRelatedToClaim.types';

interface Args {
  onNextStep: RoutedStepProps<IrshStepValues>['onNextStep'];
}

const useClaimGroupDocumentRelatedToClaim = ({ onNextStep }: Args) => {
  const { pushNotification, pushErrors } = useToasts();

  const { values } = useRoutedStepsOrchestratorContext<IrshStepValues>();
  const claimGroupId = values?.claimGroupId;

  const { claims } = useGetClaimGroupSummary({
    claimGroupId,
    suspense: true,
  });

  const reason = values?.reason;
  const isMultiProduct = claims ? claims?.length > 1 : undefined;

  const reasonSelectedAdapted = reasonAdapter({
    reasonSelected: reason,
    claimIds: claims?.map((claim) => claim.claimId) || [],
  });

  const neededInformationOnlyFileType =
    isMultiProduct && reasonSelectedAdapted
      ? reasonSelectedAdapted?.neededInformation
          ?.filter(isFileType)
          ?.filter((item) => item.relatedTo === RELATED_TO.CLAIM)
      : reasonSelectedAdapted?.neededInformation?.filter(isFileType);

  const buildAdditionalInformationDefaultValues = async () => {
    const additionalInformation = await Promise.all(
      claims?.map(async (curr: any) => {
        if (curr) {
          const fileValues = await Promise.all(
            curr.additionalClaimInformation.map(async (info: any) => {
              if (info.additionalInformationFileValue) {
                const file = await downloadFile(
                  info.additionalInformationFileValue.url,
                  info.additionalInformationFileValue.fileName,
                );

                return {
                  [info.additionalInformationId]: {
                    value: file,
                  },
                };
              }

              if (info.additionalInformationFilesValue?.length) {
                const files = await Promise.all(
                  info.additionalInformationFilesValue
                    .filter(
                      (fileValue: any) => fileValue.url && fileValue.fileName,
                    )
                    .map((fileValue: any) =>
                      downloadFile(fileValue.url, fileValue.fileName),
                    ),
                );

                return {
                  [info.additionalInformationId]: files.map((file) => ({
                    value: file,
                  })),
                };
              }

              return undefined;
            }),
          );

          return {
            reasonAdditionalInformationDocumentRelatedToClaim: Object.assign(
              {},
              ...fileValues.filter(Boolean),
            ),
          };
        }

        return undefined;
      }) || [],
    );

    return Object.assign({}, ...additionalInformation.filter(Boolean));
  };

  const formContext = useForm<ClaimGroupDocumentRelatedToClaimValues>({
    resolver: yupResolver(
      newClaimGroupDocumentPageRelatedToClaimSchema({
        reasonSelected: reasonSelectedAdapted,
        isMultiProduct,
      }),
    ),
    defaultValues: async () => await buildAdditionalInformationDefaultValues(),
    mode: REVALIDATE_MODES.ON_BLUR,
  });

  const { handleSubmit } = formContext;

  const {
    mutateAsync: handleUploadDocument,
    isLoading: isLoadingUploadDocument,
  } = useMutation(
    ['setUploadDocument'],
    async ({
      neededInformations,
      reasonAdditionalInformationDocument,
      claimId,
    }: {
      neededInformations?: ({
        internalId: string;
        fileUploadEndpoints?: Record<string, { uploadEndpoint: string }>;
      } & AdditionalInformation)[];
      reasonAdditionalInformationDocument?: Record<
        string,
        { value: File } | { value: File }[]
      >;
      claimId: string;
    }) => {
      const responseAdditionalInformationDocument =
        await uploadAdditionalInformationFilesToClaim({
          neededInformations,
          reasonAdditionalInformationDocument,
          claimId,
        });

      if (responseAdditionalInformationDocument?.some((res) => res.failure)) {
        pushNotification(
          buildNotification({
            message: messages.uploadDocumentError,
            notificationType: NOTIFICATION_TYPES.ERROR,
          }),
        );

        return undefined;
      }

      return responseAdditionalInformationDocument;
    },
  );

  const onSubmit = handleSubmit(async (reasonDocument) => {
    if (!claims) {
      return undefined;
    }

    try {
      await Promise.all(
        claims.map(async (claim) => {
          const responseAdditionalInformationDocument =
            await handleUploadDocument({
              neededInformations: neededInformationOnlyFileType,
              reasonAdditionalInformationDocument:
                reasonDocument.reasonAdditionalInformationDocumentRelatedToClaim as any,
              claimId: claim.claimId,
            });

          if (!responseAdditionalInformationDocument) {
            return undefined;
          }

          return responseAdditionalInformationDocument;
        }),
      );

      onNextStep({});
    } catch (err: any) {
      pushErrors(err);

      return undefined;
    }
  });

  return {
    neededInformationOnlyFileType,
    formContext,
    onSubmit,
    isLoadingSubmit: isLoadingUploadDocument,
  };
};

export default useClaimGroupDocumentRelatedToClaim;
